import React, { forwardRef, useState, useRef } from 'react';
import _merge from 'lodash/merge';
import { useCombinedRefs, getContext, KEY_CODES } from 'common';

import DeprecatedRippleEffect, { DeprecatedRippleEffectHandle } from '../RippleEffect/DeprecatedRippleEffect';
import styled, { MEDIUM_FAB_HEIGHT, SMALL_FAB_HEIGHT } from './styles';

const FABContext = React.createContext({ isSmall: false });

export interface FloatingActionButtonProps extends React.ComponentPropsWithRef<'div'> {
  /**  Children prop is where content resides */
  children: React.ReactNode;
  /** Style for ripple effect */
  rippleStyle?: React.CSSProperties;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** The size of the FAB. */
  size?: 'small' | 'medium';
  /** @ignore onKeyDown callback */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore onKeyUp callback */
  onKeyUp?: React.KeyboardEventHandler;
  /** @ignore onClick callback */
  onClick?: React.MouseEventHandler;
}

/** FloatingActionButton component.
 * Any other props will be provided to the root element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.)
 */
const FloatingActionButton = forwardRef<HTMLElement, FloatingActionButtonProps>((props: FloatingActionButtonProps, ref) =>
  getContext(({ RTL, theme }) => {
    const {
      children,
      rippleStyle,
      size,
      theme: propTheme,
      onKeyDown,
      onKeyUp,
      onClick,
      ...otherProps
    } = props;

    const wrapperEl = useRef();
    const rippleRef = useRef();
    const combinedRef = useCombinedRefs(ref, wrapperEl);

    const isSmall = size === 'small';
    const resolvedTheme = _merge({}, isSmall ? theme.FAB.small : theme.FAB.medium, propTheme);

    const [keyDown, setKeyDown] = useState(false);

    const handleKeyDown = (event) => {
      if (!keyDown && (event.key === KEY_CODES.ENTER_KEY || event.key === KEY_CODES.SPACE_KEY)) {
        setKeyDown(true);
        rippleRef.current
        && (rippleRef.current as DeprecatedRippleEffectHandle).handleKeyDown
        && (rippleRef.current as DeprecatedRippleEffectHandle).handleKeyDown(event);
      }
      onKeyDown && onKeyDown(event);
    };

    const handleKeyUp = (event) => {
      if (event.key === KEY_CODES.ENTER_KEY || event.key === KEY_CODES.SPACE_KEY) {
        setKeyDown(false);
      }
      onKeyUp && onKeyUp(event);
    };

    const handleClick = (event) => {
      // it is for fire-fox refocus on button after clicking
      combinedRef && combinedRef.current && combinedRef.current.focus();
      onClick && onClick(event);
    };

    return (
      <styled.ButtonBorderWrapper
        ref={combinedRef}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onClick={handleClick}
        $rtl={RTL}
        $theme={resolvedTheme}
        keyDown={keyDown}
        size={size}
        {...otherProps}
      >
        <DeprecatedRippleEffect
          ref={rippleRef}
          style={{ height: '100%', borderRadius: isSmall ? SMALL_FAB_HEIGHT : MEDIUM_FAB_HEIGHT }}
          rippleStyle={{ backgroundColor: resolvedTheme.ripple.background, ...rippleStyle }}
        >
          <styled.RippleContainer small={isSmall}>
            <FABContext.Provider value={{ isSmall }}>{children}</FABContext.Provider>
          </styled.RippleContainer>
        </DeprecatedRippleEffect>
      </styled.ButtonBorderWrapper>
    );
  }));

FloatingActionButton.displayName = 'FloatingActionButton';

FloatingActionButton.defaultProps = {
  onClick: undefined,
  rippleStyle: {},
  theme: {},
  size: 'medium',
  onKeyDown: undefined,
  onKeyUp: undefined
};

export { FloatingActionButton as default, FABContext };
